import React, { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { notificationApi, getAuthSession } from '../services/api';
import '../styles/notifications.css';

function TherapistNotifications() {
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [activeFilter, setActiveFilter] = useState('all');
  const dropdownRef = useRef(null);
  const navigate = useNavigate();
  const authSession = getAuthSession();

  // Fetch notifications - Load on mount & poll every 30s
  useEffect(() => {
    if (!authSession?.user || authSession.user.role !== 'physiotherapist') {
      return;
    }

    const loadNotifications = async () => {
      try {
        setIsLoading(true);
        const response = await notificationApi.getAll({ limit: 15 });
        const list = response.notifications || response.data || (Array.isArray(response) ? response : []);
        setNotifications(list);
        const unread = typeof response.unreadCount === 'number' 
          ? response.unreadCount 
          : list.filter(n => !n.isRead && !n.read).length;
        setUnreadCount(unread);
      } catch (error) {
        console.error('Error loading therapist notifications:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadNotifications();
    const interval = setInterval(loadNotifications, 30000);
    return () => clearInterval(interval);
  }, [authSession?.user?.role]);

  // Click outside to close dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleMarkAsRead = async (notificationId) => {
    try {
      await notificationApi.markAsRead(notificationId);
      setNotifications(prev =>
        prev.map(n => n._id === notificationId ? { ...n, isRead: true, read: true } : n)
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    try {
      await notificationApi.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, read: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const handleDelete = async (notificationId) => {
    try {
      await notificationApi.delete(notificationId);
      const target = notifications.find(n => n._id === notificationId);
      setNotifications(prev => prev.filter(n => n._id !== notificationId));
      if (!target?.isRead && !target?.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (error) {
      console.error('Error deleting notification:', error);
    }
  };

  const formatRelativeTime = (date) => {
    if (!date) return 'Recently';
    const now = new Date();
    const notifDate = new Date(date);
    const diffMs = now - notifDate;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays === 1) return 'Yesterday';
    if (diffDays < 7) return `${diffDays}d ago`;
    return notifDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  // Clean duplicate leading emojis from titles like "📅 New Booking" -> "New Booking"
  const cleanTitle = (rawTitle = '') => {
    if (!rawTitle) return 'Notification';
    return rawTitle.replace(/^[\p{Emoji}\u2000-\u3300\s]+/gu, '').trim() || rawTitle;
  };

  const getCategoryMeta = (title = '', message = '', type = '') => {
    const text = `${title} ${message} ${type}`.toLowerCase();
    if (text.includes('booking') || text.includes('appointment') || text.includes('session')) {
      return {
        category: 'booking',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
            <line x1="16" y1="2" x2="16" y2="6" />
            <line x1="8" y1="2" x2="8" y2="6" />
            <line x1="3" y1="10" x2="21" y2="10" />
          </svg>
        )
      };
    }
    if (text.includes('whatsapp') || text.includes('message') || text.includes('chat')) {
      return {
        category: 'message',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
          </svg>
        )
      };
    }
    if (text.includes('worksheet') || text.includes('clinical') || text.includes('form') || text.includes('document')) {
      return {
        category: 'worksheet',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
            <polyline points="14 2 14 8 20 8" />
            <line x1="16" y1="13" x2="8" y2="13" />
            <line x1="16" y1="17" x2="8" y2="17" />
          </svg>
        )
      };
    }
    if (text.includes('payment') || text.includes('fee') || text.includes('rupee') || text.includes('payout')) {
      return {
        category: 'payment',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="1" y="4" width="22" height="16" rx="2" ry="2" />
            <line x1="1" y1="10" x2="23" y2="10" />
          </svg>
        )
      };
    }
    if (text.includes('review') || text.includes('rating') || text.includes('feedback')) {
      return {
        category: 'review',
        icon: (
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
          </svg>
        )
      };
    }
    return {
      category: 'default',
      icon: (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="8" x2="12" y2="12" />
          <line x1="12" y1="16" x2="12.01" y2="16" />
        </svg>
      )
    };
  };

  const bookingCount = notifications.filter(n => {
    const text = `${n.title} ${n.message}`.toLowerCase();
    return text.includes('booking') || text.includes('appointment') || text.includes('session');
  }).length;

  const filteredNotifications = notifications.filter(n => {
    const isUnread = !n.isRead && !n.read;
    if (activeFilter === 'unread') return isUnread;
    if (activeFilter === 'bookings') {
      const text = `${n.title} ${n.message}`.toLowerCase();
      return text.includes('booking') || text.includes('appointment') || text.includes('session');
    }
    return true;
  });

  const handleCardClick = (notification) => {
    const isUnread = !notification.isRead && !notification.read;
    if (isUnread) {
      handleMarkAsRead(notification._id);
    }
    const text = `${notification.title} ${notification.message}`.toLowerCase();
    if (text.includes('booking') || text.includes('appointment') || text.includes('session')) {
      // Dispatch tab switch or navigate
      window.dispatchEvent(new CustomEvent('physio-navigate-tab', { detail: 'bookings' }));
      setIsOpen(false);
    } else if (text.includes('worksheet')) {
      window.dispatchEvent(new CustomEvent('physio-navigate-tab', { detail: 'worksheets' }));
      setIsOpen(false);
    }
  };

  return (
    <div className="nexaflow-notif-wrapper" ref={dropdownRef}>
      {/* Bell Trigger Button */}
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className={`nexaflow-bell-trigger ${isOpen ? 'active' : ''}`}
        title="Notifications"
        aria-label="Open notifications"
      >
        <svg
          className="nexaflow-bell-svg"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
          <path d="M13.73 21a2 2 0 0 1-3.46 0" />
        </svg>

        {unreadCount > 0 ? (
          <span className="nexaflow-badge-pill">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        ) : null}
      </button>

      {/* Popover Dropdown Panel */}
      {isOpen && (
        <div className="nexaflow-dropdown-panel">
          {/* Header */}
          <div className="nexaflow-notif-header">
            <div className="nexaflow-header-left">
              <div className="nexaflow-header-icon">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                  <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                </svg>
              </div>
              <h3 className="nexaflow-header-title">Notifications</h3>
              {unreadCount > 0 && (
                <span className="nexaflow-count-chip">{unreadCount} New</span>
              )}
            </div>

            <div className="nexaflow-header-actions">
              {unreadCount > 0 && (
                <button
                  type="button"
                  onClick={handleMarkAllAsRead}
                  className="nexaflow-mark-read-btn"
                  title="Mark all as read"
                >
                  <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <polyline points="20 6 9 17 4 12" />
                  </svg>
                  <span>Mark all read</span>
                </button>
              )}
              <button
                type="button"
                onClick={() => setIsOpen(false)}
                className="nexaflow-header-close-btn"
                title="Close"
              >
                ✕
              </button>
            </div>
          </div>

          {/* Filter Bar */}
          <div className="nexaflow-filter-bar">
            <button
              type="button"
              className={`nexaflow-filter-tab ${activeFilter === 'all' ? 'active' : ''}`}
              onClick={() => setActiveFilter('all')}
            >
              All
              <span className="nexaflow-tab-pill">{notifications.length}</span>
            </button>
            <button
              type="button"
              className={`nexaflow-filter-tab ${activeFilter === 'unread' ? 'active' : ''}`}
              onClick={() => setActiveFilter('unread')}
            >
              Unread
              <span className="nexaflow-tab-pill">{unreadCount}</span>
            </button>
            {bookingCount > 0 && (
              <button
                type="button"
                className={`nexaflow-filter-tab ${activeFilter === 'bookings' ? 'active' : ''}`}
                onClick={() => setActiveFilter('bookings')}
              >
                Bookings
                <span className="nexaflow-tab-pill">{bookingCount}</span>
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="nexaflow-notif-list">
            {isLoading ? (
              <div className="nexaflow-loading-box">
                <div className="nexaflow-spinner"></div>
                <p style={{ margin: 0, fontSize: '13px' }}>Loading notifications...</p>
              </div>
            ) : filteredNotifications.length === 0 ? (
              <div className="nexaflow-empty-box">
                <div className="nexaflow-empty-icon-wrap">
                  <svg width="26" height="26" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9" />
                    <path d="M13.73 21a2 2 0 0 1-3.46 0" />
                  </svg>
                </div>
                <h4 className="nexaflow-empty-title">All caught up!</h4>
                <p className="nexaflow-empty-desc">
                  {activeFilter === 'unread' 
                    ? "You don't have any unread notifications right now."
                    : "You will receive real-time updates when patients book consultations or submit clinical worksheets."}
                </p>
              </div>
            ) : (
              filteredNotifications.map((notification) => {
                const isUnread = !notification.isRead && !notification.read;
                const { category, icon } = getCategoryMeta(notification.title, notification.message, notification.type);
                const title = cleanTitle(notification.title);

                return (
                  <div
                    key={notification._id}
                    className={`nexaflow-notif-card ${isUnread ? 'unread' : 'read'}`}
                    onClick={() => handleCardClick(notification)}
                  >
                    {/* Category Icon Badge */}
                    <div className={`nexaflow-cat-avatar ${category}`}>
                      {icon}
                    </div>

                    {/* Card Content */}
                    <div className="nexaflow-card-body">
                      <div className="nexaflow-card-row1">
                        <h4 className="nexaflow-card-title">{title}</h4>
                        <span className="nexaflow-card-time">
                          <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <polyline points="12 6 12 12 16 14" />
                          </svg>
                          {formatRelativeTime(notification.createdAt)}
                        </span>
                      </div>

                      <p className="nexaflow-card-msg">{notification.message}</p>

                      {/* Metadata Chips */}
                      {(notification.clientName || notification.sessionDate || notification.sessionMode) && (
                        <div className="nexaflow-chips-row">
                          {notification.clientName && (
                            <span className="nexaflow-meta-chip client">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                                <circle cx="12" cy="7" r="4" />
                              </svg>
                              {notification.clientName}
                            </span>
                          )}
                          {notification.sessionDate && (
                            <span className="nexaflow-meta-chip">
                              <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <rect x="3" y="4" width="18" height="18" rx="2" ry="2" />
                                <line x1="16" y1="2" x2="16" y2="6" />
                                <line x1="8" y1="2" x2="8" y2="6" />
                              </svg>
                              {new Date(notification.sessionDate).toLocaleDateString([], { month: 'short', day: 'numeric' })}
                            </span>
                          )}
                          {notification.sessionMode && (
                            <span className={`nexaflow-meta-chip ${notification.sessionMode === 'online' ? 'mode-online' : 'mode-clinic'}`}>
                              {notification.sessionMode === 'online' ? '💻 Online' : '🏥 Clinic'}
                            </span>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Quick Actions */}
                    <div className="nexaflow-card-actions">
                      {isUnread && (
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleMarkAsRead(notification._id);
                          }}
                          className="nexaflow-action-btn read"
                          title="Mark as read"
                        >
                          <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="20 6 9 17 4 12" />
                          </svg>
                        </button>
                      )}
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleDelete(notification._id);
                        }}
                        className="nexaflow-action-btn delete"
                        title="Delete notification"
                      >
                        <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                          <line x1="18" y1="6" x2="6" y2="18" />
                          <line x1="6" y1="6" x2="18" y2="18" />
                        </svg>
                      </button>
                    </div>
                  </div>
                );
              })
            )}
          </div>

          {/* Footer */}
          <div className="nexaflow-notif-footer">
            <div className="nexaflow-live-sync-pill">
              <span className="nexaflow-live-dot"></span>
              <span>Syncing with PsychMitra Cloud</span>
            </div>
            <button
              type="button"
              onClick={() => {
                window.dispatchEvent(new CustomEvent('physio-navigate-tab', { detail: 'bookings' }));
                setIsOpen(false);
              }}
              className="nexaflow-footer-cta"
            >
              View Bookings →
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default TherapistNotifications;
